import { createReducer, on } from '@ngrx/store';
import {
  setSaveStatus,
  setAppStatus,
  signQr,
  createPasBundle,
  submitPasBundle,
  submitFailure,
  submitPasBundleSuccess,
  setPersistentError,
  setReqFieldCounter,
  setLogError,
  saveQr,
  updateQr,
  saveQrSuccess,
  updateQrSuccess,
  setInitialValues,
  resetState,
  setAutoSave,
  setValidationError,
  nextLandingStage,
  submitAttachment,
  submitAttachmentSuccess,
  nextQuestion,
  nextQuestionSuccess,
  inquireClaim,
  inquireClaimSuccess,
  inquireClaimFailure,
  loadSubmissionStatusSuccess,
  loadSubmissionStatusFailure,
} from './form.actions';
import { AppState, AppStatus } from './app.state';
import cloneDeep from 'lodash/cloneDeep';
import { Status } from '../models/Status';

export const initialState: AppState = {
  appStatus: AppStatus.INIT,
  saveStatus: undefined,
  landing: {
    stages: [
      { text: 'Landing.LoadApp', status: 'TODO' },
      { text: 'Landing.RetrievingQuestionnaire', status: 'TODO' },
      { text: 'Landing.PopulatingQuestionnaire', status: 'TODO' },
      { text: 'Landing.RenderingQuestionnaire', status: 'TODO' },
    ],
    currentStage: -1,
  },
  submit: {
    stages: [
      { text: 'Submit.SavingQr', status: 'TODO' },
      { text: 'Submit.CreatingPasBundle', status: 'TODO' },
      { text: 'Submit.SubmittingPasBundle', status: 'TODO' },
    ],
    currentStage: -1,
  },
  persistentError: undefined,
  reqFieldCounter: {
    required: 0,
    answered: 0,
    width: '0%',
  },
  logError: [],
  questionnaire: undefined,
  questionnaireResponse: undefined,
  populateError: undefined,
  task: undefined,
  autoSave: true,
};

export const formReducer = createReducer(
  initialState,
  on(setAutoSave, (state: AppState, { autoSave }): AppState => {
    return { ...state, autoSave };
  }),
  on(nextLandingStage, (state: AppState): AppState => {
    return nextStage(state, 'landing');
  }),
  on(setAppStatus, (state: AppState, { appStatus }): AppState => {
    const resetState = resetSubmitStage(state, state.task);
    return { ...resetState, appStatus, saveStatus: Status.SAVED };
  }),
  on(setSaveStatus, (state: AppState, { saveStatus }): AppState => {
    return { ...state, saveStatus };
  }),
  on(
    setInitialValues,
    (
      state: AppState,
      {
        questionnaire,
        questionnaireResponse,
        populateError,
        task,
        pasClaimIdentifier,
        orderReference,
      },
    ): AppState => {
      const resetState = resetSubmitStage(state, task);
      return {
        ...resetState,
        questionnaire,
        questionnaireResponse,
        populateError,
        task,
        pasClaimIdentifier,
        orderReference,
        claim: undefined,
        claimResponse: undefined,
        inquiring: false,
        appStatus: AppStatus.INPROGRESS,
        saveStatus: Status.SAVED,
      };
    },
  ),
  on(setValidationError, (state: AppState): AppState => {
    return { ...state, hasValidationError: true, saveStatus: Status.UNSAVED };
  }),
  on(updateQr, (state: AppState, { detail }): AppState => {
    return {
      ...state,
      saveStatus: Status.SPINNER,
      detail,
      hasValidationError: undefined,
    };
  }),
  on(updateQrSuccess, (state: AppState, { qr }): AppState => {
    return { ...state, saveStatus: Status.UNSAVED, questionnaireResponse: qr };
  }),
  on(saveQr, (state: AppState): AppState => {
    return { ...state, saveStatus: Status.SPINNER };
  }),
  on(saveQrSuccess, (state: AppState, { qr }): AppState => {
    return { ...state, questionnaireResponse: qr, saveStatus: Status.SAVED };
  }),
  on(nextQuestion, (state: AppState): AppState => {
    return {
      ...state,
      saveStatus: Status.NEXT_QUESTION,
      hasValidationError: undefined,
    };
  }),
  on(
    nextQuestionSuccess,
    (state: AppState, { qr, questionnaire }): AppState => {
      return {
        ...state,
        saveStatus: Status.UNSAVED,
        questionnaire,
        questionnaireResponse: qr,
      };
    },
  ),
  on(signQr, (state: AppState): AppState => {
    return nextSubmitStage({
      ...state,
      saveStatus: undefined,
      appStatus: AppStatus.SUBMITTING,
    });
  }),
  on(createPasBundle, (state: AppState): AppState => {
    return nextSubmitStage(state);
  }),
  on(submitPasBundle, (state: AppState): AppState => {
    return nextSubmitStage(state);
  }),
  on(submitPasBundleSuccess, (state: AppState): AppState => {
    return { ...state, appStatus: AppStatus.DONE };
  }),
  on(submitAttachment, (state: AppState): AppState => {
    return nextSubmitStage(state);
  }),
  on(submitAttachmentSuccess, (state: AppState): AppState => {
    return { ...state, appStatus: AppStatus.DONE };
  }),
  on(submitFailure, (state: AppState, { error }): AppState => {
    const newState = cloneDeep(state);
    newState.submit.stages[newState.submit.currentStage].status = 'ERROR';
    newState.persistentError = {
      title: errorMessage(error),
      body: 'Submit.ErrorMessage',
    };
    return newState;
  }),
  on(inquireClaim, (state: AppState): AppState => {
    return { ...state, inquiring: true, persistentError: undefined };
  }),
  on(
    inquireClaimSuccess,
    (state: AppState, { claim, claimResponse }): AppState => {
      return { ...state, inquiring: false, claim, claimResponse };
    },
  ),
  on(inquireClaimFailure, (state: AppState, { error }): AppState => {
    return {
      ...state,
      inquiring: false,
      persistentError: {
        title: errorMessage(error),
        body: 'Submit.InquiryErrorMessage',
      },
    };
  }),
  on(
    loadSubmissionStatusSuccess,
    (state: AppState, { claim, claimResponse }): AppState => {
      return { ...state, claim, claimResponse };
    },
  ),
  on(loadSubmissionStatusFailure, (state: AppState, { error }): AppState => {
    return {
      ...state,
      persistentError: {
        title: errorMessage(error),
        body: 'SubmissionStatus.LoadErrorMessage',
      },
    };
  }),
  on(setPersistentError, (state: AppState, { persistentError }): AppState => {
    const newLanding = cloneDeep(state.landing);
    const stage = newLanding.stages[newLanding.currentStage];
    if (stage) {
      stage.status = 'ERROR';
    }
    return { ...state, persistentError, landing: newLanding };
  }),
  on(setReqFieldCounter, (state: AppState, { counter }): AppState => {
    return {
      ...state,
      reqFieldCounter: {
        ...counter,
        width:
          counter.required === 0
            ? '0%'
            : (counter.answered / counter.required) * 100 + '%',
      },
    };
  }),
  on(
    setLogError,
    (state: AppState, { questionnaireErrors, optional, invalid }): AppState => {
      const logError = [questionnaireErrors, optional, invalid].reduce(
        (prev, opOutcome) =>
          opOutcome?.issue ? prev.concat(opOutcome.issue) : prev,
        [],
      );
      return { ...state, logError };
    },
  ),
  on(resetState, (): AppState => {
    return initialState;
  }),
);

function nextStage(state: AppState, label: string) {
  const stateCopy = cloneDeep(state);
  let currentStage = stateCopy[label].currentStage;
  if (currentStage >= 0) {
    stateCopy[label].stages[currentStage].status = 'DONE';
  }
  currentStage++;
  if (currentStage < stateCopy[label].stages.length) {
    stateCopy[label].stages[currentStage].status = 'INPROGRESS';
  }
  stateCopy[label].currentStage = currentStage;
  return stateCopy;
}

function errorMessage(error): string {
  if (!error) {
    return 'Submit.UnknownError';
  }
  if (error.statusCode) {
    return error.statusCode + ' ' + error.statusText;
  }
  return error.message ?? error;
}

function nextSubmitStage(state: AppState) {
  return nextStage(state, 'submit');
}

function resetSubmitStage(state: AppState, task?) {
  return {
    ...state,
    submit: {
      stages: task
        ? [
            { text: 'Submit.SavingQr', status: 'TODO' },
            { text: 'Submit.SubmittingQuestionnaireCdex', status: 'TODO' },
          ]
        : [
            { text: 'Submit.SavingQr', status: 'TODO' },
            { text: 'Submit.CreatingPasBundle', status: 'TODO' },
            { text: 'Submit.SubmittingPasBundle', status: 'TODO' },
          ],
      currentStage: -1,
    },
    persistentError: undefined,
  };
}
